import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import { useFeatureFlags } from '@cowprotocol/common-hooks'
import { areAddressesEqual, isEvmChain } from '@cowprotocol/cow-sdk'
import { isSafeWalletAtom, useWalletInfo } from '@cowprotocol/wallet'

import { useDeployedCowShedAddresses } from './useDeployedCowShedAddresses'

import {
  ADVANCED_ORDERS_ACCOUNT_PROXY_CONFIG,
  ACCOUNT_PROXY_CONFIGS,
  SHOW_ONLY_DEPLOYED_ACCOUNT_PROXIES,
} from '../accountProxy.constants'
import { getCowShedHooks } from '../utils/getCowShedHooks'
import { isEvmProxyOwner } from '../utils/isEvmProxyOwner'

import type { AccountProxyInfo } from '../accountProxy.types'

export function useAccountProxies(): AccountProxyInfo[] | null {
  const { chainId, account } = useWalletInfo()
  const isSafeWallet = useAtomValue(isSafeWalletAtom)
  const { isTwapEoaEnabled } = useFeatureFlags()
  const deployedAddresses = useDeployedCowShedAddresses(
    SHOW_ONLY_DEPLOYED_ACCOUNT_PROXIES ? account : undefined,
    SHOW_ONLY_DEPLOYED_ACCOUNT_PROXIES && isEvmChain(chainId) ? chainId : undefined,
  )

  return useMemo(() => {
    if (!isEvmProxyOwner(account, chainId)) return null

    return ACCOUNT_PROXY_CONFIGS.reduce<AccountProxyInfo[]>((proxies, config) => {
      if (config.id === ADVANCED_ORDERS_ACCOUNT_PROXY_CONFIG.id && (!isTwapEoaEnabled || isSafeWallet)) {
        return proxies
      }

      const sdk = getCowShedHooks({ chainId, accountProxyConfig: config })
      const proxyAccount = sdk.proxyOf(account)
      const isDeployed = deployedAddresses?.some((address) => areAddressesEqual(address, proxyAccount)) ?? false

      const isAlwaysShown = 'alwaysShow' in config && config.alwaysShow

      if (SHOW_ONLY_DEPLOYED_ACCOUNT_PROXIES && !isAlwaysShown && !isDeployed) return proxies

      proxies.push({
        ...config,
        sdk,
        account: proxyAccount,
      })

      return proxies
    }, [])
  }, [chainId, account, deployedAddresses, isTwapEoaEnabled, isSafeWallet])
}
